import type { ImageRef } from '../../content/site';
import { TiltCard } from './TiltCard';

type ShotProps = {
	image: ImageRef;
	/** Grid placement and spacing. */
	className?: string;
	/** A phone capture gets the softer corners of a phone screen (no device drawn around it). */
	screen?: 'desktop' | 'phone';
	/** Display `sizes` hint of the image. */
	sizes?: string;
	/** Fixed box (CSS aspect-ratio, e.g. "16 / 10"): the capture fills it, cropped from the top. */
	ratio?: string;
};

const SCREEN = {
	desktop: 'rounded-card',
	phone: 'rounded-[clamp(1.25rem,3vw,1.75rem)]',
};

/** Sharp on retina: never displayed wider than its intrinsic width divided by this. */
const DENSITY = 1.6;

/**
 * A real screenshot, one level only: the image with its radius and one soft, green-tinted
 * shadow (a hairline in the dark theme, where a shadow would not show). No mat, no frame,
 * no overlay of fake chrome: the capture speaks alone. It floats (TiltCard): on a mouse it
 * tilts toward the cursor and catches a soft light; the image itself is never scaled. It
 * is never shown wider than its intrinsic width / 1.6, whatever the column offers.
 */
export function Shot({ image, className = '', screen = 'desktop', sizes, ratio }: ShotProps) {
	const maxWidth = Math.floor(image.width / DENSITY);
	return (
		<figure className={className} style={{ maxWidth }}>
			<TiltCard radius={SCREEN[screen]}>
				<img
					src={image.src}
					width={image.width}
					height={image.height}
					alt={image.alt}
					sizes={sizes ?? `(min-width: 1024px) ${maxWidth}px, 100vw`}
					loading="lazy"
					decoding="async"
					style={ratio ? { aspectRatio: ratio } : undefined}
					className={`block w-full ${ratio ? 'h-auto object-cover object-top' : 'h-auto'}`}
				/>
			</TiltCard>
		</figure>
	);
}
